<template>
    <v-dialog v-model="priceModal" max-width="1000px" class="price-modal">
        <v-card>
            <v-toolbar dark color="primary">
                <v-card-title>{{ getModalName(priceModalType) | t }} </v-card-title>
                <v-spacer />
                <v-btn @click="handleCancelClick" icon>
                    <v-icon>close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text>
                <v-container class="price-modal-container">
                    <v-simple-table v-if="priceData && priceData.length > 0">
                        <template v-slot:default>
                            <thead>
                                <tr>
                                    <th class="text-left">
                                        {{ t("Start date") }}
                                    </th>
                                    <th class="text-left">
                                        {{ t("Price") }}
                                    </th>
                                    <th v-if="allowedEditRoles.includes(user.role)"></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, idx) in getSortedPrices" :key="item.id">
                                    <td :class="{ 'has-error': item.errors && item.errors.includes('start_date') }">
                                        <date-picker
                                            v-model="item.start_date"
                                            :popover="{ placement: 'bottom', visibility: 'click' }"
                                            @input="checkForRules"
                                        >
                                            <v-text-field
                                                :value="getDate(item.start_date)"
                                                append-icon="fas fa-calendar"
                                                class="text-field-item mt-3 mb-3"
                                                dense
                                                :disabled="
                                                    !hasPermissionForAction(
                                                        PermissionsConstants.PRODUCT,
                                                        PermissionsConstants.CHANGE_PRICE
                                                    )
                                                "
                                                hide-details
                                                outlined
                                            />
                                        </date-picker>
                                    </td>
                                    <td>
                                        <v-text-field
                                            :value="item.value"
                                            type="number"
                                            step="0.1"
                                            class="mt-3 mb-3"
                                            dense
                                            hide-details
                                            :disabled="
                                                !hasPermissionForAction(
                                                    PermissionsConstants.PRODUCT,
                                                    PermissionsConstants.CHANGE_PRICE
                                                )
                                            "
                                            outlined
                                            @input="updateField($event, 'value', idx)"
                                        ></v-text-field>
                                    </td>
                                    <td
                                        v-if="
                                            hasPermissionForAction(
                                                PermissionsConstants.PRODUCT,
                                                PermissionsConstants.CHANGE_PRICE
                                            )
                                        "
                                    >
                                        <v-icon class="ml-1" small @click="handleDeleteClick(idx)"
                                            >fas fa-trash-alt
                                        </v-icon>
                                    </td>
                                </tr>
                            </tbody>
                        </template>
                    </v-simple-table>
                    <div v-else class="text-center">{{ t("No data to show") }}</div>
                </v-container>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                    color="bal-red darken-1"
                    text
                    @click="handleAddClick()"
                    v-if="hasPermissionForAction(PermissionsConstants.PRODUCT, PermissionsConstants.CHANGE_PRICE)"
                    >{{ "Add" | t }}</v-btn
                >
                <v-btn color="bal-red darken-1" text @click="handleCancelClick">{{ "Cancel" | t }}</v-btn>
                <v-btn
                    color="bal-red darken-1"
                    text
                    @click="handleSaveClick"
                    v-if="hasPermissionForAction(PermissionsConstants.PRODUCT, PermissionsConstants.CHANGE_PRICE)"
                    >{{ "Save" | t }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import moment from "moment";
import DatePicker from "v-calendar/lib/components/date-picker.umd";
import { PermissionsConstants } from "@/permissions";

export default {
    name: "product-price-modal",
    components: { DatePicker },
    data() {
        return {
            allowedEditRoles: ["superadmin"]
        };
    },
    async mounted() {
        document.addEventListener("keydown", this.handleKeyboardListener);
        await this.getPriceTypeData();
        this.priceData.forEach(p => {
            p.start_date = moment(p.start_date, "DD.MM.YYYY").toDate();
        });
        await this.checkForRules();
    },
    computed: {
        ...mapGetters(["hasPermissionForAction"]),
        PermissionsConstants() {
            return PermissionsConstants;
        },
        ...mapState("ProductStore", ["priceModal", "activeProduct", "priceData", "priceModalType", "requestError"]),
        ...mapState(["user"]),
        getSortedPrices() {
            return this.priceData.sort(function(a, b) {
                return b.start_date - a.start_date;
            });
        }
    },
    watch: {},
    async beforeDestroy() {
        await this.handleCancelClick();
    },
    methods: {
        ...mapMutations("ProductStore", [
            "setPriceModal",
            "addPriceEntry",
            "removePriceEntry",
            "updatePriceDataValue",
            "setRequestError",
            "setErrorMessage"
        ]),
        ...mapActions("ProductStore", ["getPriceTypeData", "saveProductPriceData", "getProductData"]),
        async handleCancelClick() {
            document.removeEventListener("keydown", this.handleKeyboardListener);
            await this.setPriceModal(false);
            await this.getProductData(this.activeProduct.id);
        },
        async handleSaveClick() {
            if (this.checkForRules()) {
                this.setErrorMessage(this.t("Fix errors first"));
                this.setRequestError(true);
                return;
            }
            const copyOfPriceData = [...this.priceData];
            copyOfPriceData.forEach(p => {
                p.start_date = moment(p.start_date).format("DD.MM.YYYY");
            });
            let response = await this.saveProductPriceData(copyOfPriceData);
            if (response) return this.setPriceModal(false);
            await this.setErrorMessage(this.t("Fix errors first"));
            await this.setRequestError(true);
        },
        async handleAddClick() {
            const payload = {
                date:
                    this.getSortedPrices.length > 0
                        ? moment(this.getSortedPrices[0].start_date).add(7, "days")
                        : new Date(),
                value: this.getSortedPrices.length > 0 ? parseFloat(this.getSortedPrices[0].value) + 0.025 : 0
            };
            await this.addPriceEntry(payload);
            this.checkForRules();
        },
        async handleDeleteClick(index) {
            await this.removePriceEntry(index);
            this.checkForRules();
        },
        async updateField(value, field, index) {
            await this.updatePriceDataValue({ prop: field, value: value, index: index });
        },
        getDate(value) {
            if (value) return moment(value).format("DD.MM.YYYY");
            else return null;
        },
        checkForRules() {
            if (this.getSortedPrices.length === 0) return true;
            let hasError = false;
            let firstDate = null;
            this.getSortedPrices.forEach(p => {
                p.errors = [];
                if (
                    !p.start_date ||
                    (firstDate && moment(p.start_date).format("DD.MM.YYYY") === moment(firstDate).format("DD.MM.YYYY"))
                ) {
                    p.errors.push("start_date");
                    hasError = true;
                }
                firstDate = p.start_date;
            });
            return hasError;
        },
        getModalName(name) {
            if (name === "list_net_price") return "Price list price";
            else if (name === "export_price") return "Export price";
            else if (name === "cost_price") return "Cost price";
            else if (name === "own_price") return "Own price";
            else if (name === "base_net_price") return "Base Net price";
        }
    }
};
</script>

<style scoped lang="scss">
::v-deep .v-dialog {
    overflow-y: initial !important;
}
.has-error {
    border: red solid 1px !important;
    border-radius: 5px;
}
.price-modal-container,
.price-modal {
    max-height: 600px !important;
}
.price-modal-container {
    overflow: auto;
}
</style>
